import { useEffect, useRef, useState } from "react";
import type { Score } from "./types";

/**
 * Locks the engraved score to Jason's video, so the notation follows his actual
 * hands rather than a metronome.
 *
 * The alignment model is the one proven on the Riff Database's Live Mode:
 *
 *     videoTime = rate * musicTime + offset
 *
 * `offset` is where in the video the performance's first beat lands and `rate` is
 * a small clock-drift correction (about 1.000x). Both are measured once per piece
 * and stored on the score as `midiSync`.
 *
 * The Riff implementation had to fight the YouTube IFrame API, which only exposes
 * a polled `getCurrentTime()`; it polled at 4 Hz, extrapolated between polls with
 * `performance.now()`, and chased the result with a critically damped smoother.
 * EFL videos are ordinary files on Bunny CDN played in a real <video> element, so
 * we can read `currentTime` directly and, where the browser supports it, get a
 * callback on every presented frame. That is strictly more accurate with less
 * machinery — but the damped chase is kept, because `currentTime` still updates
 * in visible steps on some browsers and an undamped playhead visibly stutters.
 */

/**
 * `requestVideoFrameCallback` is not in every lib.dom.d.ts and, where it is, it
 * is declared as required — so it is picked off the element structurally rather
 * than by extending HTMLVideoElement, which would conflict.
 */
type FrameCapableVideo = HTMLVideoElement & Partial<{
  requestVideoFrameCallback: (cb: (now: number, meta: unknown) => void) => number;
  cancelVideoFrameCallback: (handle: number) => void;
}>;

export interface VideoSyncState {
  /** Index into score.notes of the note Jason is playing now, or null. */
  playingIndex: number | null;
  /** Position in the piece, in quarter notes. */
  beat: number;
  playing: boolean;
  /** Live nudge applied on top of the stored offset, in seconds. */
  nudge: number;
  setNudge: (seconds: number) => void;
}

export function useVideoScoreSync(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  score: Score,
): VideoSyncState {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [beat, setBeat] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [nudge, setNudge] = useState(0);

  const smoothRef = useRef(0);
  const lastFrameRef = useRef(0);
  const nudgeRef = useRef(0);
  nudgeRef.current = nudge;

  // Sounding notes, sorted, so the lookup per frame is a short scan from a
  // remembered cursor rather than a search of the whole piece.
  const soundingRef = useRef<{ start: number; end: number; index: number }[]>([]);
  useEffect(() => {
    soundingRef.current = score.notes
      .map((n, index) => ({ n, index }))
      .filter(({ n }) => !n.rest)
      .map(({ n, index }) => ({ start: n.start, end: n.start + n.dur, index }))
      .sort((a, b) => a.start - b.start);
  }, [score]);

  useEffect(() => {
    const video = videoRef.current as FrameCapableVideo | null;
    if (!video) return;
    const sync = score.midiSync;
    if (!sync) return;

    const secsPerBeat = 60 / score.tempo;
    let running = true;
    let rafHandle = 0;
    let frameHandle = 0;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onPause);

    const step = () => {
      if (!running) return;
      const now = performance.now() / 1000;
      const dt = Math.min(0.1, now - (lastFrameRef.current || now));
      lastFrameRef.current = now;

      const target = video.currentTime;
      // Snap on a seek, glide on ordinary drift. Without this the playhead
      // visibly steps whenever currentTime updates coarsely.
      if (Math.abs(target - smoothRef.current) > 0.6) smoothRef.current = target;
      else smoothRef.current += (target - smoothRef.current) * Math.min(1, 12 * dt);

      const rate = sync.rate || 1;
      const musicSecs = (smoothRef.current - sync.offset - nudgeRef.current) / rate;
      const beats = musicSecs / secsPerBeat;
      setBeat(beats);

      let found: number | null = null;
      for (const s of soundingRef.current) {
        if (s.start > beats) break;
        if (beats < s.end) { found = s.index; break; }
      }
      setPlayingIndex(found);

      if (video.requestVideoFrameCallback) {
        frameHandle = video.requestVideoFrameCallback(step);
      } else {
        rafHandle = requestAnimationFrame(step);
      }
    };

    if (video.requestVideoFrameCallback) {
      frameHandle = video.requestVideoFrameCallback(step);
    } else {
      rafHandle = requestAnimationFrame(step);
    }

    return () => {
      running = false;
      cancelAnimationFrame(rafHandle);
      video.cancelVideoFrameCallback?.(frameHandle);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onPause);
    };
  }, [score, videoRef]);

  return { playingIndex, beat, playing, nudge, setNudge };
}
